import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatValue } from '@/charts/palette'
import { tidyLabel } from '@/features/aufmass/types'
import { fmtDate, fmtNumber, useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { reconciliationMessages } from '@/i18n/messages/reconciliation'

interface Summary {
  workbook_rows: number; aufmass_rows: number
  workbook_metri: number; aufmass_metri: number
  workbook_sub: number; aufmass_sub: number
  days_metri_within_1pct: number; days_total: number
  positions_within_1pct: number; positions_total: number
}
interface DayRow {
  work_date: string; workbook_rows: number; aufmass_rows: number
  workbook_metri: number; aufmass_metri: number; metri_delta: number
  metri_pct: number | null; workbook_sub: number; aufmass_sub: number; sub_pct: number | null
}
interface PositionRow {
  position_key: number; label: string | null
  workbook_qty: number; aufmass_qty: number; delta: number; pct: number | null
}
interface Recon { summary: Summary; byDay: DayRow[]; byPosition: PositionRow[] }

export function ReconciliationPage() {
  const t = useT(reconciliationMessages)
  const tc = useT(commonMessages)
  const { data, isPending } = useQuery<Recon>({
    queryKey: ['reconciliation'],
    queryFn: () => api.get<Recon>('/api/reconciliation'),
  })

  if (isPending) return <p className="text-sm text-neutral-500">{tc('loading')}</p>
  if (!data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('couldNotLoad')}</p>

  const s = data.summary
  const pct = (a: number, b: number) => (b === 0 ? null : (100 * a) / b)

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
        <p className="mt-1 max-w-3xl text-sm text-neutral-500">
          {t('intro')}
        </p>
      </header>

      <section className="grid gap-3 sm:grid-cols-3">
        <Compare label={t('metres')} ours={s.aufmass_metri} theirs={s.workbook_metri} unit="m" />
        <Compare label={t('subRevenue')} ours={s.aufmass_sub} theirs={s.workbook_sub} unit="EUR" />
        <Compare label={t('rows')} ours={s.aufmass_rows} theirs={s.workbook_rows} unit="" />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <Panel
          title={t('byPosition')}
          subtitle={t('withinOnePct', { within: s.positions_within_1pct, total: s.positions_total })}
        >
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="pb-2 font-medium">{t('colPosition')}</th>
                <th className="pb-2 text-right font-medium">{t('colWorkbook')}</th>
                <th className="pb-2 text-right font-medium">Aufmaß</th>
                <th className="pb-2 text-right font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {data.byPosition
                .slice()
                .sort((a, b) => b.workbook_qty - a.workbook_qty)
                .map((p) => (
                  <tr key={p.position_key} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                    <td className="max-w-[14rem] truncate py-1.5" title={tidyLabel(p.label ?? '')}>
                      {tidyLabel(p.label ?? `#${p.position_key}`)}
                    </td>
                    <td className="tabular py-1.5 text-right">{fmtNumber(Math.round(p.workbook_qty))}</td>
                    <td className="tabular py-1.5 text-right">{fmtNumber(Math.round(p.aufmass_qty))}</td>
                    <td className="tabular py-1.5 text-right"><Pct value={p.pct} /></td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Panel>

        <Panel title={t('byWorkingDay')} subtitle={t('withinOnePct', { within: s.days_metri_within_1pct, total: s.days_total })}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="pb-2 font-medium">{t('colDate')}</th>
                <th className="pb-2 text-right font-medium">{t('colRows')}</th>
                <th className="pb-2 text-right font-medium">{t('colMetres')}</th>
                <th className="pb-2 text-right font-medium">%</th>
              </tr>
            </thead>
            <tbody>
              {data.byDay.map((d) => (
                <tr key={d.work_date} className="border-b border-neutral-100 last:border-0 dark:border-neutral-800/60">
                  <td className="py-1.5">{fmtDate(d.work_date)}</td>
                  <td className="tabular py-1.5 text-right text-neutral-500">
                    {d.aufmass_rows}/{d.workbook_rows}
                  </td>
                  <td className="tabular py-1.5 text-right">
                    {fmtNumber(Math.round(d.aufmass_metri))}
                  </td>
                  <td className="tabular py-1.5 text-right"><Pct value={d.metri_pct} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </section>
    </div>
  )

  function Compare({ label, ours, theirs, unit }: {
    label: string; ours: number; theirs: number; unit: string
  }) {
    const p = pct(ours, theirs)
    return (
      <div className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="text-xs text-neutral-500">{label}</div>
        <div className="tabular mt-1 text-2xl font-semibold tracking-tight">
          {formatValue(ours, unit)}
        </div>
        <div className="mt-1 text-xs text-neutral-500">
          {t('workbook')} <span className="tabular">{formatValue(theirs, unit)}</span>
        </div>
        {p != null && (
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
            <div
              className="h-full rounded-full"
              style={{
                width: `${Math.min(p, 100)}%`,
                backgroundColor: p >= 99 ? 'var(--viz-good)' : 'var(--viz-warning)',
              }}
            />
          </div>
        )}
        {p != null && <div className="tabular mt-1 text-xs text-neutral-500">{fmtNumber(p, 1)}%</div>}
      </div>
    )
  }
}

/** Colour AND a glyph, so the state survives for a colourblind reader. */
function Pct({ value }: { value: number | null }) {
  if (value == null) return <span className="text-neutral-400">—</span>
  const ok = value >= 99 && value <= 101
  const over = value > 101
  return (
    <span style={{ color: ok ? 'var(--viz-good)' : over ? 'var(--viz-critical)' : 'var(--viz-warning)' }}>
      <span aria-hidden>{ok ? '✓' : over ? '▲' : '▼'}</span> {fmtNumber(value)}%
    </span>
  )
}

function Panel({ title, subtitle, children }: {
  title: string; subtitle: string; children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h3 className="text-sm font-semibold">{title}</h3>
      <p className="mt-0.5 text-xs text-neutral-500">{subtitle}</p>
      <div className="mt-3 max-h-[30rem] overflow-y-auto">{children}</div>
    </div>
  )
}
